/**
 * useOverlapResolution - Hook for managing meal overlap resolution
 * 
 * This hook provides state management and resolution logic for handling
 * meal scheduling conflicts in the meal plan editor.
 */

import { useState, useCallback, useMemo } from 'react';
import {
    initializeOverlapActions,
    resolveOverlaps,
    validateOverlapActions,
    getAvailableActions,
    sortOverlaps
} from '@utils/overlapResolution';

/**
 * Hook for managing overlap resolution
 * @param {Object} config - Configuration object
 * @param {Array} config.overlaps - Initial overlap data
 * @param {Array} config.meals - Additional meals being moved
 * @param {string} config.mode - 'reschedule' or 'repeat'
 * @param {Function} config.onComplete - Callback when resolution is complete
 * @param {Array} config.clears - Existing clear slots
 * @param {number} config.targetOffset - Target offset for additional meals
 * @param {string} config.targetMealType - Target meal type for additional meals
 * @param {Array} config.mealTypes - Array of meal type objects with name property
 * @param {boolean} config.returnMode - Whether we're in return mode (excludes swap)
 * @param {boolean} config.isRecipeSearch - Whether we're creating leftovers from recipe search (excludes swap)
 * @returns {Object} Overlap resolution state and methods
 */
export function useOverlapResolution(config = {}) {
    const {
        overlaps: initialOverlaps = [],
        meals = [],
        mode = 'reschedule',
        onComplete,
        clears = [],
        targetOffset = null,
        targetMealType = null,
        mealTypes = [],
        returnMode = false,
        isRecipeSearch = false
    } = config;
    // Initialize overlap actions
    const [overlapActions, setOverlapActions] = useState(() =>
        initializeOverlapActions(initialOverlaps)
    );

    // Get available actions for current mode
    const availableActions = useMemo(() =>
        getAvailableActions(mode, returnMode, isRecipeSearch),
        [mode, returnMode, isRecipeSearch]
    );
    
    // Sort overlaps for display (with original indices preserved)
    const sortedOverlaps = useMemo(() => {
        // Add original index to each overlap before sorting
        const withIndices = overlapActions.map((overlap, index) => ({
            ...overlap,
            originalIndex: index
        }));
        return sortOverlaps(withIndices, mealTypes);
    }, [overlapActions, mealTypes]);
    
    /**
     * Update action for a specific overlap
     * @param {number} index - Index in the original array
     * @param {string} action - New action to set
     */
    const updateOverlapAction = useCallback((index, action) => {
        setOverlapActions(prev => {
            const updated = [...prev];
            updated[index] = {
                ...updated[index],
                action
            };
            return updated;
        });
    }, []);
    
    /**
     * Check if all overlaps have valid actions
     */
    const canResolve = useMemo(() => 
        overlapActions.length > 0,
        [overlapActions]
    );
    
    /**
     * Resolve all overlaps and trigger completion callback
     */
    const resolveAndComplete = useCallback(() => {
        const resolution = resolveOverlaps(overlapActions, {
            mode,
            additionalMeals: meals,
            targetMealType,
            targetOffset,
            existingClears: clears
        });
        
        if (onComplete) {
            onComplete(resolution);
        }
    }, [
        overlapActions,
        mode,
        meals,
        targetMealType,
        targetOffset,
        clears,
        canResolve,
        onComplete
    ]);
    
    /**
     * Reset all actions to default
     */
    const resetActions = useCallback((defaultAction = 'replace') => {
        setOverlapActions(initializeOverlapActions(initialOverlaps, defaultAction));
    }, [initialOverlaps]);
    
    /**
     * Get summary of current resolution
     */
    const getResolutionSummary = useCallback(() => {
        const summary = {
            totalOverlaps: overlapActions.length,
            actions: {
                swap: 0,
                replace: 0,
                combine: 0
            }
        };
        
        overlapActions.forEach(overlap => {
            if (overlap.action && summary.actions[overlap.action] !== undefined) {
                summary.actions[overlap.action]++;
            }
        });
        
        return summary;
    }, [overlapActions]);
    
    return {
        // State
        overlapActions: overlapActions, // Unsorted original array
        sortedOverlaps,                  // Sorted array for display
        availableActions,
        canResolve,
        
        // Methods
        updateOverlapAction,
        resolveAndComplete,
        resetActions,
        getResolutionSummary,
        
        // Computed
        hasOverlaps: overlapActions.length > 0,
        mode
    };
}

export default useOverlapResolution;